import { useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useHttpClient } from '@/modules/shared/hooks/use-http-client'
import { useOperationOrder } from '../hooks/use-operations-order.hook'
import { useOrderFormStore, useOrderFormStoreActions } from '../stores/order-form.store'
import { useOrderFormParamsQuery } from '../querys/order-form-params.query'
import { useOrderFormParamsLayoutQuery } from '../querys/order-form-params-layout.query'
import type { IOrderFormJson } from '../types/order-form-json.type'
import { newOrderService } from '../services/new-order.service'
import { orderFormJsonEditService } from '../services/edit-order.service'
import { orderFormJsonViewService } from '../services/order-form-json-view.service'


export const useLoadOrder = () => {
	const navigate = useNavigate()
	const routerParams = useParams()
	const { setCompanyId } = useOperationOrder()

	const { operationType, orderSelected, paramsOrderForm, jsonOrderForm } =
		useOrderFormStore()

	const {
		setJsonOrderForm,
		setParamsOrderForm,
		setEntityTypeSelected,
		setParamsLayoutOrderForm,
	} = useOrderFormStoreActions()

	const { orderFormParams, refetchOrderFormParams } = useOrderFormParamsQuery({
		companyId: 0,
	})

	const { orderFormParamsLayout, refetchOrderFormParamsLayout } =
		useOrderFormParamsLayoutQuery({ paramsType: 'pedido' })

	const processOrderData = useCallback((orderData: IOrderFormJson) => {
		setJsonOrderForm(orderData)
		const entityType = orderData?.cliente?.flagPessoa === '2' ? '1' : '2'
		setEntityTypeSelected(entityType)

		const hasMultipleParams = paramsOrderForm?.listaParametros?.length > 1
		if (!hasMultipleParams || operationType !== 'add') {
			setCompanyId(orderData.fkEmpresa)
		}
	}, [setJsonOrderForm, setEntityTypeSelected, paramsOrderForm, operationType, setCompanyId])

	const handleError = useCallback(() => {
		setTimeout(() => navigate('/pedidos'), 3000)
	}, [navigate])

	const newOrderClient = useHttpClient(newOrderService, {
		onSuccess: (response) => {
			const orderData: IOrderFormJson = JSON.parse(response.body)
			processOrderData(orderData)
		},
		onError: handleError,
		showToast: true,
		showSucessToast: false,
	})

	const editOrderClient = useHttpClient(orderFormJsonEditService, {
		onSuccess: (response) => {
			const orderData: IOrderFormJson = JSON.parse(response.body)
			processOrderData(orderData)
		},
		onError: handleError,
		showToast: true,
		showSucessToast: false,
	})

	const viewOrderClient = useHttpClient(orderFormJsonViewService, {
		onSuccess: (response) => {
			const orderData: IOrderFormJson = JSON.parse(response.body)
			processOrderData(orderData)
		},
		onError: handleError,
		showToast: true,
		showSucessToast: false,
	})

	const loadNewOrder = useCallback(() => {
		newOrderClient.execute({})
	}, [newOrderClient])

	const loadEditOrder = useCallback(() => {
		editOrderClient.execute({
			payload: {
				orderId: orderSelected?.idPedidoCel ?? Number.parseInt(routerParams.id),
			},
		})
	}, [editOrderClient, orderSelected, routerParams.id])

	const loadViewOrder = useCallback(() => {
		viewOrderClient.execute({
			payload: {
				orderId: orderSelected?.idPedidoCel ?? routerParams.id,
				userId: orderSelected?.fkUsuarioCel,
				dispositivoId: orderSelected?.fkUsuarioxDispositivo,
			},
		})
	}, [viewOrderClient, orderSelected, routerParams.id])

	const loadOrderByOperationType = useCallback(() => {
		if (jsonOrderForm?.idPedidoCel) {
			processOrderData(jsonOrderForm)
			return
		}

		switch (operationType) {
			case 'add':
				loadNewOrder()
				break
			case 'edit':
				loadEditOrder()
				break
			case 'view':
				loadViewOrder()
				break
		}
	}, [
		jsonOrderForm,
		processOrderData,
		operationType,
		loadNewOrder,
		loadEditOrder,
		loadViewOrder,
	])

	useEffect(() => {
		if (!jsonOrderForm?.idPedidoCel) {
			refetchOrderFormParamsLayout()
		}
	}, [jsonOrderForm, refetchOrderFormParamsLayout])

	useEffect(() => {
		if (orderFormParamsLayout) {
			setParamsLayoutOrderForm(orderFormParamsLayout)
			refetchOrderFormParams()
		}
	}, [orderFormParamsLayout])

	useEffect(() => {
		if (orderFormParams) {
			setParamsOrderForm(orderFormParams)
			loadOrderByOperationType()
		}
	}, [orderFormParams])

	return {
		isLoadingNewOrder: newOrderClient.isLoading,
		isLoadingEditOrder: editOrderClient.isLoading,
		isLoadingViewOrder: viewOrderClient.isLoading,

		isLoading: newOrderClient.isLoading || editOrderClient.isLoading || viewOrderClient.isLoading,

		errors: {
			newOrder: newOrderClient.error,
			editOrder: editOrderClient.error,
			viewOrder: viewOrderClient.error,
		},
		data: {
			newOrder: newOrderClient.data,
			editOrder: editOrderClient.data,
			viewOrder: viewOrderClient.data,
		},

		retryNewOrder: loadNewOrder,
		retryEditOrder: loadEditOrder,
		retryViewOrder: loadViewOrder,

		resetNewOrder: newOrderClient.reset,
		resetEditOrder: editOrderClient.reset,
		resetViewOrder: viewOrderClient.reset,
	}
}